import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	ArrowRight,
	BarChart3,
	Bot,
	CalendarRange,
	Check,
	ClipboardCheck,
	ClipboardList,
	Crosshair,
	FileDown,
	FileUp,
	Gauge,
	Image,
	Info,
	Languages,
	Layers,
	Lightbulb,
	Link2,
	MessageSquareText,
	Pencil,
	Plug,
	Route,
	Scissors,
	ShieldCheck,
	Sparkles,
	Target,
	Trophy,
	UserSearch,
	Users,
	Waves,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getAgentGuide } from "../../lib/aiAgent";
import { DOMAIN_SHOOTING, filterByDomain } from "../../lib/domainAccess";
import { describeScope, isDestructiveScope } from "../../lib/mcp";
import { cn } from "../../lib/utils";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

/**
 * «Guida AI» (`/ai-guide`): cosa si può chiedere all'assistente, con esempi.
 *
 * I testi e gli esempi arrivano dal server (`GET /ai-agent/guide`, da
 * `backend/services/ai_tools/guide.py`): ogni esempio è legato ai tool che
 * usa, e i permessi mostrati sono quelli che il consenso chiede davvero. Qui
 * dentro resta solo la presentazione.
 *
 * La pagina è fatta per non scorrere all'infinito: tre tab, e negli esempi
 * un'area alla volta con un esempio aperto alla volta. La prima versione
 * mostrava tutto in fila ed era una pagina che non si riusciva a seguire.
 */

// Le icone sono componenti, quindi il server ne manda il nome. Un nome che
// qui non c'è cade su una stella invece di rompere la pagina: va aggiunto qui.
const ICONS = {
	BarChart3,
	CalendarRange,
	ClipboardCheck,
	ClipboardList,
	Crosshair,
	FileDown,
	FileUp,
	Gauge,
	Image,
	Languages,
	Layers,
	Link2,
	MessageSquareText,
	Pencil,
	Route,
	Scissors,
	Target,
	Trophy,
	UserSearch,
	Users,
	Waves,
};

function iconFor(name) {
	return ICONS[name] || Sparkles;
}

// Etichette corte per i chip. Un permesso nuovo senza voce qui mostra il suo
// nome tecnico: visibile, quindi da aggiungere, non nascosto.
const SCOPE_SHORT = {
	"programs:read": "Lettura",
	"programs:write": "Creare e correggere",
	"programs:modify": "Cancellare",
	"competitions:write": "Gare",
	"models:write": "Modelli prestativi",
};

function ScopeChip({ scope }) {
	return (
		<span
			title={`Permesso del connettore: ${describeScope(scope)}`}
			className={cn(
				"rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-wide",
				isDestructiveScope(scope)
					? "border-blaze/30 bg-blaze/10 text-blaze"
					: "border-border text-muted-foreground",
			)}
		>
			{SCOPE_SHORT[scope] || scope}
		</span>
	);
}

function ExampleItem({ useCase }) {
	const Icon = iconFor(useCase.icon);
	return (
		<AccordionItem
			value={useCase.id}
			className="rounded-sm border border-border bg-card px-3 data-[state=open]:border-volt/40"
			data-testid={`ai-guide-case-${useCase.id}`}
		>
			<AccordionTrigger className="gap-3 py-3 hover:no-underline">
				<span className="flex min-w-0 flex-1 items-start gap-3">
					<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-volt/10 text-volt">
						<Icon className="h-4 w-4" />
					</span>
					<span className="min-w-0">
						<span className="block font-medium leading-snug">
							{useCase.title}
						</span>
						<span className="mt-0.5 block text-xs font-normal text-muted-foreground line-clamp-2">
							{useCase.why}
						</span>
					</span>
				</span>
			</AccordionTrigger>
			<AccordionContent className="space-y-3">
				<figure className="rounded-sm border-l-2 border-volt bg-secondary/40 p-3">
					<figcaption className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
						Prova a chiedere
					</figcaption>
					<blockquote className="break-words text-sm leading-relaxed">
						{useCase.prompt}
					</blockquote>
				</figure>

				<div>
					<p className="mb-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
						Cosa fa l'assistente
					</p>
					<ul className="space-y-1 text-xs">
						{useCase.does.map((step) => (
							<li key={step} className="flex gap-1.5">
								<Check className="mt-0.5 h-3 w-3 shrink-0 text-volt" />
								<span className="min-w-0">{step}</span>
							</li>
						))}
					</ul>
				</div>

				{useCase.note && (
					<p className="flex gap-1.5 text-xs text-muted-foreground">
						<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						<span className="min-w-0">{useCase.note}</span>
					</p>
				)}

				<div className="space-y-2 border-t border-border pt-3">
					<div className="flex flex-wrap gap-1.5">
						{useCase.scopes.map((scope) => (
							<ScopeChip key={scope} scope={scope} />
						))}
					</div>
					<p className="flex items-start gap-1 text-[11px] text-muted-foreground">
						{useCase.inApp ? (
							<>
								<Bot className="mt-px h-3 w-3 shrink-0" />
								<span className="min-w-0">Anche in app · {useCase.inApp}</span>
							</>
						) : (
							<>
								<Plug className="mt-px h-3 w-3 shrink-0" />
								<span className="min-w-0">Solo col connettore</span>
							</>
						)}
					</p>
				</div>
			</AccordionContent>
		</AccordionItem>
	);
}

/**
 * Le aree: elenco verticale da `md` in su, menu a tendina sotto. Su un
 * telefono sette pulsanti in fila andrebbero a capo su tre righe prima ancora
 * di arrivare agli esempi.
 */
function CategoryPicker({ sections, value, onChange }) {
	return (
		<>
			<div className="md:hidden">
				<Select value={value} onValueChange={onChange}>
					<SelectTrigger className="h-11 w-full rounded-sm border-border bg-secondary">
						<SelectValue />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						{sections.map((section) => (
							<SelectItem key={section.id} value={section.id}>
								{section.label} ({section.cases.length})
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<nav className="hidden self-start md:sticky md:top-4 md:flex md:flex-col md:gap-1">
				{sections.map((section) => {
					const Icon = iconFor(section.icon);
					const active = section.id === value;
					return (
						<button
							key={section.id}
							type="button"
							onClick={() => onChange(section.id)}
							aria-current={active ? "true" : undefined}
							data-testid={`ai-guide-category-${section.id}`}
							className={cn(
								"flex items-center gap-2 rounded-sm border px-3 py-2 text-left text-sm transition-colors",
								active
									? "border-volt/50 bg-volt/10 text-foreground"
									: "border-transparent text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
							)}
						>
							<Icon className={cn("h-4 w-4 shrink-0", active && "text-volt")} />
							<span className="min-w-0 flex-1">{section.label}</span>
							<span className="text-xs text-muted-foreground">
								{section.cases.length}
							</span>
						</button>
					);
				})}
			</nav>
		</>
	);
}

function ExamplesTab({ sections }) {
	const [category, setCategory] = useState(null);
	const active =
		sections.find((section) => section.id === category) || sections[0];
	if (!active) return null;

	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
			<CategoryPicker
				sections={sections}
				value={active.id}
				onChange={setCategory}
			/>
			{/* La `key` riapre il primo esempio a ogni cambio d'area: chi arriva
			    su un'area vede subito una richiesta, non un elenco di titoli. */}
			<Accordion
				key={active.id}
				type="single"
				collapsible
				defaultValue={active.cases[0]?.id}
				className="min-w-0 space-y-2"
			>
				{active.cases.map((useCase) => (
					<ExampleItem key={useCase.id} useCase={useCase} />
				))}
			</Accordion>
		</div>
	);
}

function WorkflowTab({ steps }) {
	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				Le capacità si sommano: dal controllo del gruppo al PDF per l'atleta,
				in una conversazione sola e senza cambiare finestra.
			</p>
			<ol className="ml-3 space-y-5 border-l border-border pl-6">
				{steps.map((step, index) => (
					<li key={step.label} className="relative">
						<span className="absolute -left-9 top-0 flex h-6 w-6 items-center justify-center rounded-sm bg-volt font-heading text-xs text-black">
							{index + 1}
						</span>
						<p className="text-xs uppercase tracking-widest text-muted-foreground">
							{step.label}
						</p>
						<p className="mt-1 break-words text-sm leading-snug">
							«{step.prompt}»
						</p>
						<p className="mt-1.5 flex gap-1.5 text-xs text-muted-foreground">
							<ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-volt" />
							<span className="min-w-0">{step.result}</span>
						</p>
					</li>
				))}
			</ol>
		</div>
	);
}

function RulesTab({ staysWithYou, tips }) {
	return (
		<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
			<section>
				<h2 className="font-heading text-lg">Cosa resta a te</h2>
				<p className="mb-3 text-sm text-muted-foreground">
					Quello che l'assistente non fa da solo, e perché puoi lasciarlo
					lavorare tranquillo.
				</p>
				<ul className="space-y-2">
					{staysWithYou.map((item) => (
						<li
							key={item.title}
							className="flex gap-2 rounded-sm border border-border bg-card p-3"
						>
							<ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
							<p className="min-w-0 text-sm">
								<span className="font-medium">{item.title}.</span>{" "}
								<span className="text-muted-foreground">{item.text}</span>
							</p>
						</li>
					))}
				</ul>
			</section>
			<section>
				<h2 className="font-heading text-lg">Come chiedere bene</h2>
				<p className="mb-3 text-sm text-muted-foreground">
					Abitudini che fanno la differenza fra una risposta generica e una
					tua.
				</p>
				<ul className="space-y-2">
					{tips.map((tip) => (
						<li
							key={tip}
							className="flex gap-2 rounded-sm border border-border bg-card p-3 text-sm"
						>
							<Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
							<span className="min-w-0">{tip}</span>
						</li>
					))}
				</ul>
			</section>
		</div>
	);
}

function ChannelTile({ icon: Icon, title, badge, children }) {
	return (
		<div className="rounded-sm border border-border bg-card p-3">
			<p className="mb-1 flex flex-wrap items-center gap-x-1.5 text-sm font-medium">
				<Icon className="h-4 w-4 shrink-0 text-volt" />
				{title}
				{badge && (
					<span className="text-[10px] uppercase tracking-wide text-volt">
						{badge}
					</span>
				)}
			</p>
			<p className="text-xs text-muted-foreground">{children}</p>
		</div>
	);
}

const TAB_TRIGGER =
	"min-w-0 rounded-sm px-2 text-xs sm:text-sm data-[state=active]:bg-card";

export function AIGuideTab() {
	const { user } = useAuth();
	const [guide, setGuide] = useState(null);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		getAgentGuide()
			.then((data) => !cancelled && setGuide(data))
			.catch(() => !cancelled && setFailed(true));
		return () => {
			cancelled = true;
		};
	}, []);

	// Gli esempi di un dominio che il coach non pratica (il tiro, per chi fa
	// solo fisico) non si mostrano: come le voci di menu. Il tiro in più e'
	// ristretto agli account che lo hanno nel proprio profilo, admin compreso:
	// `hasDomain` darebbe all'admin tutto, e la guida del tiro è per ora solo
	// per chi è abilitato davvero.
	const sections = useMemo(() => {
		const hasShooting = (user?.domains || []).includes(DOMAIN_SHOOTING);
		const visible = (guide?.examples || []).filter(
			(useCase) => useCase.domain !== DOMAIN_SHOOTING || hasShooting,
		);
		const useCases = filterByDomain(visible, user);
		return (guide?.categories || [])
			.map((item) => ({
				...item,
				cases: useCases.filter((useCase) => useCase.category === item.id),
			}))
			.filter((item) => item.cases.length > 0);
	}, [guide, user]);

	return (
		<div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
			<header className="space-y-4">
				<div>
					<p className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-volt">
						<Sparkles className="h-3.5 w-3.5" />
						Guida AI
					</p>
					<h1 className="font-heading text-2xl md:text-3xl">
						Cosa puoi fare con l'AI
					</h1>
					<p className="mt-1 max-w-3xl text-sm text-muted-foreground md:text-base">
						L'assistente legge atleti, risultati, programmi, gare e modelli
						prestativi, e scrive direttamente nell'app. Negli esempi trovi
						richieste vere: cambia nomi e date con i tuoi, e parti da lì.
					</p>
				</div>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<ChannelTile
						icon={Plug}
						title="Connettore · Claude o ChatGPT"
						badge="consigliato"
					>
						Tutto quello che vedi qui, file allegati compresi. Lavori nella
						chat che usi già, col tuo abbonamento.
					</ChannelTile>
					<ChannelTile icon={Bot} title="Chat nell'app">
						Gli stessi strumenti, a consumo sulla tua chiave API. Scrive solo
						sul programma o sul modello da cui la apri, e non legge file.
					</ChannelTile>
				</div>

				<Button
					asChild
					className="w-full rounded-sm bg-volt text-black hover:bg-volt/90 sm:w-auto"
				>
					<Link to="/ai-assistant">
						Collega l'assistente
						<ArrowRight className="ml-1.5 h-4 w-4" />
					</Link>
				</Button>
			</header>

			{failed && (
				<p className="text-sm text-blaze">
					Non riesco a caricare la guida. Ricarica la pagina tra un momento.
				</p>
			)}

			{!guide && !failed && (
				<p className="text-sm text-muted-foreground">Carico la guida…</p>
			)}

			{guide && (
				<Tabs defaultValue="examples">
					<TabsList className="grid h-auto w-full grid-cols-3 rounded-sm bg-secondary p-1">
						<TabsTrigger value="examples" className={TAB_TRIGGER}>
							Esempi
						</TabsTrigger>
						<TabsTrigger value="workflow" className={TAB_TRIGGER}>
							Caso completo
						</TabsTrigger>
						<TabsTrigger value="rules" className={TAB_TRIGGER}>
							<span className="sm:hidden">Consigli</span>
							<span className="hidden sm:inline">Regole e consigli</span>
						</TabsTrigger>
					</TabsList>

					<TabsContent value="examples" className="mt-4">
						<ExamplesTab sections={sections} />
					</TabsContent>
					<TabsContent value="workflow" className="mt-4">
						<WorkflowTab steps={guide.workflow} />
					</TabsContent>
					<TabsContent value="rules" className="mt-4">
						<RulesTab staysWithYou={guide.stays_with_you} tips={guide.tips} />
					</TabsContent>
				</Tabs>
			)}
		</div>
	);
}
